import { Link } from 'react-router-dom';
import type { TherapistSpecialization } from '@lighthouse/shared';

const SPECIALTIES: {
  label: string;
  specialization: TherapistSpecialization;
  icon: 'brain' | 'cloud' | 'heart' | 'shield' | 'person' | 'leaf';
}[] = [
  { label: 'Anxiety', specialization: 'anxiety', icon: 'brain' },
  { label: 'Depression', specialization: 'depression', icon: 'cloud' },
  { label: 'Relationships', specialization: 'relationship_couples', icon: 'heart' },
  { label: 'Trauma', specialization: 'trauma_ptsd', icon: 'shield' },
  { label: 'Youth', specialization: 'adolescent_youth', icon: 'person' },
  { label: 'Stress', specialization: 'stress_burnout', icon: 'leaf' },
];

export function AssessmentLeafCard() {
  return (
    <Link
      to="/assessment"
      className="relative block overflow-hidden rounded-3xl bg-[#F3F6F1] p-6 sm:p-8"
    >
      <span className="pointer-events-none absolute bottom-2 right-3 text-[#C5D7C4]" aria-hidden>
        <LeafIcon className="h-16 w-16 rotate-[18deg]" />
      </span>
      <span className="pointer-events-none absolute bottom-8 right-14 text-[#D5E4D2]" aria-hidden>
        <LeafIcon className="h-10 w-10 -rotate-[24deg]" />
      </span>
      <span className="flex items-center gap-3">
        <span className="bg-glacier text-midnight inline-flex h-8 w-8 items-center justify-center rounded-full">
          <SproutIcon />
        </span>
        <span className="font-serif text-xl font-medium text-[#1C1917]">
          Not sure where to start?
        </span>
      </span>
      <span className="text-slateink mt-2 block max-w-xl text-sm leading-5">
        Take a short assessment and we&apos;ll help you find a therapist that fits your needs.
      </span>
      <span className="text-midnight mt-3 block text-[15px] font-bold">Take the assessment →</span>
    </Link>
  );
}

export function SpecialtyPills() {
  return (
    <div>
      <h2 className="font-serif text-3xl font-medium text-[#1C1917]">Browse by specialty</h2>
      <div className="mt-4 flex flex-wrap gap-2.5">
        {SPECIALTIES.map((item) => (
          <Link
            key={item.specialization}
            to={`/therapists?specialization=${item.specialization}`}
            className="bg-glacier text-midnight inline-flex items-center gap-1.5 rounded-full px-3.5 py-2.5 text-sm font-semibold"
          >
            <SpecialtyIcon name={item.icon} />
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function SpecialtyIcon({ name }: { name: (typeof SPECIALTIES)[number]['icon'] }) {
  const common = 'h-4 w-4';
  if (name === 'brain') return <BrainIcon className={common} />;
  if (name === 'cloud') return <CloudIcon className={common} />;
  if (name === 'heart') return <HeartIcon className={common} />;
  if (name === 'shield') return <ShieldIcon className={common} />;
  if (name === 'person') return <PersonIcon className={common} />;
  return <LeafIcon className={common} />;
}

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17 8C8 10 5.9 16.2 4 20c2.5-3.5 6-5.1 10-5.1.3 2.2-.2 4.2-1.2 6.1 4.2-.4 7.2-3.4 8.2-7.5C22.2 9.2 20 8.2 17 8z" />
    </svg>
  );
}

function SproutIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path d="M12 21V12" strokeWidth="1.75" strokeLinecap="round" />
      <path
        d="M12 12c0-4 2.4-6.5 6.5-7.5C17.2 8.5 15 11 12 12z"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M12 12C12 8 9.6 5.5 5.5 4.5 6.8 8.5 9 11 12 12z"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BrainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        d="M9 7a3 3 0 015.2-2M8 8a3 3 0 00-3 3c0 1 .5 1.8 1.2 2.3A3 3 0 008 19h1.2M16 8a3 3 0 013 3c0 1-.5 1.8-1.2 2.3A3 3 0 0116 19h-1.2M9.2 19V10.5M14.8 19V10.5M9.2 13h5.6"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 18h9.2A4.2 4.2 0 0017 10.2 5.5 5.5 0 007.2 9 3.8 3.8 0 007 18z" />
    </svg>
  );
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        d="M12 19s-6.2-3.8-6.2-8A3.4 3.4 0 0112 8.2 3.4 3.4 0 0118.2 11c0 4.2-6.2 8-6.2 8z"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path d="M12 3.5l7 2.5v6.2c0 4.2-2.8 7.2-7 8.3-4.2-1.1-7-4.1-7-8.3V6z" strokeWidth="1.6" />
    </svg>
  );
}

function PersonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="12" cy="8" r="3" strokeWidth="1.6" />
      <path
        d="M6.5 19.5c.8-3 2.8-4.5 5.5-4.5s4.7 1.5 5.5 4.5"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
